import { useColorMode } from '@chakra-ui/react';
import { RainbowKitProvider, darkTheme, lightTheme, useConnectModal } from '@rainbow-me/rainbowkit';
import React from 'react';
import { WagmiProvider } from 'wagmi';

import config from 'configs/app';
import wagmiConfig from 'lib/web3/wagmiConfig';
import colors from 'theme/foundations/colors';
import { BODY_TYPEFACE } from 'theme/foundations/typography';

const feature = config.features.blockchainInteraction;

let openConnectModalRef: (() => void) | null = null;

// Exported function to open wallet modal from anywhere (no React hook needed)
export function openAppKitModal() {
  if (openConnectModalRef) {
    openConnectModalRef();
    return Promise.resolve();
  }
  // eslint-disable-next-line no-console
  console.warn('[Web3ModalProvider] Wallet modal is not ready yet');
  return Promise.resolve();
}

export function isAppKitReady() {
  return openConnectModalRef !== null;
}

interface Props {
  children: React.ReactNode;
}

const WalletModalBridge = () => {
  const { openConnectModal } = useConnectModal();

  React.useEffect(() => {
    openConnectModalRef = openConnectModal ?? null;

    return () => {
      if (openConnectModalRef === openConnectModal) {
        openConnectModalRef = null;
      }
    };
  }, [ openConnectModal ]);

  return null;
};

const DefaultProvider = ({ children }: Props) => {
  return (
    <WagmiProvider config={ wagmiConfig.config }>
      { children }
    </WagmiProvider>
  );
};

const Web3ModalProvider = ({ children }: Props) => {
  const { colorMode } = useColorMode();
  const theme = React.useMemo(() => {
    const baseTheme = colorMode === 'dark' ? darkTheme() : lightTheme();

    return {
      ...baseTheme,
      fonts: {
        body: `${ BODY_TYPEFACE }, sans-serif`,
      },
      radii: {
        ...baseTheme.radii,
        actionButton: '6px',
        connectButton: '6px',
        menuButton: '6px',
        modal: '8px',
        modalMobile: '8px',
      },
      colors: {
        ...baseTheme.colors,
        accentColor: colors.blue[600],
      },
      blurs: {
        ...baseTheme.blurs,
        modalOverlay: '2px',
      },
    };
  }, [ colorMode ]);

  return (
    <DefaultProvider>
      <RainbowKitProvider theme={ theme } modalSize="compact">
        <WalletModalBridge/>
        { children }
      </RainbowKitProvider>
    </DefaultProvider>
  );
};

const Provider = feature.isEnabled ? Web3ModalProvider : DefaultProvider;

export default Provider;
